'use client';

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import ComparablesMap from './ComparablesMap';

interface PropertyDetailsFormProps {
  dictionary: any;
  propertyType: 'apartment' | 'house';
  municipality: string;
  cityName: string;
  onSubmit: (data: PropertyDetails) => void;
  disableSubmit?: boolean;
  // Prefill for session-resume
  initialValues?: Partial<PropertyDetails>;
  // Subject coordinates to show nearby comparables
  subject_latitude?: number;
  subject_longitude?: number;
  // Session cache for nearby comparables
  initialNearbyComparables?: Array<{ id: string; lat: number; lng: number; address?: string }>;
  onNearbyComparables?: (comps: Array<{ id: string; lat: number; lng: number; address?: string }>) => void;
  // Pre-fetch support
  nearbyPreFetchPromise?: Promise<Array<{ id: string; lat: number; lng: number; address?: string }>>;
}

interface PropertyDetails {
  livingArea: number;
  bedrooms: number;
  bathrooms: number;
  plotArea?: number;
  facade?: number;
  transactionCount?: number;
}

interface MunicipalityStats {
  medianPrice: number;
  growthRate: number;
  propertyCount: number;
}

export default function PropertyDetailsForm({
  dictionary,
  propertyType,
  municipality,
  cityName,
  onSubmit,
  disableSubmit = false,
  initialValues,
  subject_latitude,
  subject_longitude,
  initialNearbyComparables,
  onNearbyComparables,
  nearbyPreFetchPromise,
}: PropertyDetailsFormProps) {
  const [errors, setErrors] = useState<{
    livingArea?: string;
    bedrooms?: string;
    bathrooms?: string;
    plotArea?: string;
    facade?: string;
  }>({});
  const [stats, setStats] = useState<MunicipalityStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [visibleStage, setVisibleStage] = useState(0);
  const coordsAvailable = typeof subject_latitude === 'number' && typeof subject_longitude === 'number';
  const [showForm, setShowForm] = useState<boolean>(!coordsAvailable);
  const [userToggledOpen, setUserToggledOpen] = useState(false);
  const gated = coordsAvailable && !userToggledOpen;
  const [nearbyComparables, setNearbyComparables] = useState<any[] | undefined>(initialNearbyComparables);

  // keep local state in sync if parent provides a cache later
  useEffect(() => {
    if (initialNearbyComparables && (!nearbyComparables || nearbyComparables.length === 0)) {
      setNearbyComparables(initialNearbyComparables);
    }
  }, [initialNearbyComparables, nearbyComparables]);
  // Stage 0: Living Area + Plot Area (if house) visible
  // Stage 1: Bedrooms visible
  // Stage 2: Bathrooms visible
  // Stage 3: Facade Radios (if house) OR Button (if apartment) visible
  // Stage 4: Button (if house) visible

  // State for controlled inputs
  const [livingArea, setLivingArea] = useState<number | ''>('');
  const [bedrooms, setBedrooms] = useState<number | ''>('');
  const [bathrooms, setBathrooms] = useState<number | ''>('');
  const [plotArea, setPlotArea] = useState<number | ''>('');
  const [facadeValue, setFacadeValue] = useState<number | null>(null); // No default selection

  // Prefill from initialValues
  useEffect(() => {
    try {
      if (!initialValues) return;
      if (typeof initialValues.livingArea === 'number') setLivingArea(initialValues.livingArea);
      if (typeof initialValues.bedrooms === 'number') setBedrooms(initialValues.bedrooms);
      if (typeof initialValues.bathrooms === 'number') setBathrooms(initialValues.bathrooms);
      if (typeof initialValues.plotArea === 'number') setPlotArea(initialValues.plotArea);
      if (typeof initialValues.facade === 'number') setFacadeValue(initialValues.facade);
      // Compute visibleStage based on provided values
      let stage = 0;
      const hasLiving = typeof initialValues.livingArea === 'number' && initialValues.livingArea > 0;
      const hasPlot = propertyType === 'house' ? (typeof initialValues.plotArea === 'number' && initialValues.plotArea > 0) : true;
      if (hasLiving && hasPlot) stage = 1;
      const hasBeds = typeof initialValues.bedrooms === 'number' && initialValues.bedrooms >= 0;
      if (stage >= 1 && hasBeds) stage = 2;
      const hasBaths = typeof initialValues.bathrooms === 'number' && initialValues.bathrooms >= 0;
      if (stage >= 2 && hasBaths) stage = 3;
      const hasFacade = propertyType === 'house' ? typeof initialValues.facade === 'number' : true;
      if (propertyType === 'house' && stage >= 3 && hasFacade) stage = 4;
      setVisibleStage(stage);
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If coords later become available and user hasn't opened the form yet, keep it hidden until Continue is clicked
  useEffect(() => {
    if (coordsAvailable && !userToggledOpen) {
      setShowForm(false);
    }
  }, [coordsAvailable, userToggledOpen]);

  // Fetch stats when component mounts
  useEffect(() => {
    const fetchStats = async () => {
      setIsLoadingStats(true);
      try {
        const response = await fetch('/api/municipality-stats', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            municipality,
            propertyType: propertyType
          }),
        });

        if (response.ok) {
          const stats = await response.json();
          setStats(stats);
        }
      } catch (error) {
        console.error('Error fetching real estate statistics:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    fetchStats();
  }, [propertyType, municipality]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const livingArea = formData.get('livingArea') as string;
    const bedrooms = formData.get('bedrooms') as string;
    const bathrooms = formData.get('bathrooms') as string;

    let plotArea = null;
    let facadeValueToSend = null;

    if (propertyType === 'house') {
      plotArea = formData.get('plotArea') as string;
      facadeValueToSend = facadeValue; // Get from state
    }

    const newErrors: typeof errors = {};

    if (!livingArea || isNaN(Number(livingArea)) || Number(livingArea) <= 0) {
      newErrors.livingArea = dictionary?.sellingForm?.livingAreaRequired || 'Please enter a valid living area';
    }
    if (bedrooms === '' || isNaN(Number(bedrooms)) || Number(bedrooms) < 0) {
      newErrors.bedrooms = dictionary?.sellingForm?.bedroomsRequired || 'Please enter a valid number of bedrooms';
    }
    if (bathrooms === '' || isNaN(Number(bathrooms)) || Number(bathrooms) < 0) {
      newErrors.bathrooms = dictionary?.sellingForm?.bathroomsRequired || 'Please enter a valid number of bathrooms';
    }

    if (propertyType === 'house') {
      if (!plotArea || isNaN(Number(plotArea)) || Number(plotArea) <= 0) {
        newErrors.plotArea = dictionary?.sellingForm?.plotAreaRequired || 'Please enter a valid plot area';
      }
      // Add validation for facadeValue if needed (e.g., ensure it's not null)
      if (facadeValue === null) {
        // Maybe set an error or rely on default?
        // For now, assume default (4) is okay or handled by radio selection
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const dataToSubmit: PropertyDetails = {
      livingArea: Number(livingArea),
      bedrooms: Number(bedrooms),
      bathrooms: Number(bathrooms),
      plotArea: plotArea ? Number(plotArea) : undefined,
      facade: facadeValueToSend === null ? undefined : facadeValueToSend,
      transactionCount: stats?.propertyCount
    };

    onSubmit(dataToSubmit);
  };


  return (
    <>
      {/* Map preview with Continue toggle */}
      {(typeof subject_latitude === 'number' && typeof subject_longitude === 'number') && (
        <div className="w-full">
          <p className="text-base md:text-sm text-zinc-800 pb-2">
            {(() => {
              const template = dictionary?.sellingForm?.mapDetailIntro || 'Here below you see all [property type] that were recently sold in your neighborhood. 💰';
              let translatedPropertyType;
              
              if (propertyType === 'house') {
                const houses = dictionary?.sellingForm?.propertyTypeHouses || 'houses';
                // For French, add the appropriate article
                if (houses === 'maisons') {
                  translatedPropertyType = 'toutes les maisons';
                } else {
                  translatedPropertyType = houses.toLowerCase();
                }
              } else {
                const apartments = dictionary?.sellingForm?.propertyTypeApartments || 'apartments';
                // For French, add the appropriate article
                if (apartments === 'appartements') {
                  translatedPropertyType = 'tous les appartements';
                } else {
                  translatedPropertyType = apartments.toLowerCase();
                }
              }
              
              return template.replace('[property type]', translatedPropertyType);
            })()}
          </p>
          <ComparablesMap
            dictionary={dictionary}
            subject_latitude={subject_latitude}
            subject_longitude={subject_longitude}
            propertyType={propertyType}
            initialComparables={nearbyComparables}
            onComparablesLoaded={(comps) => { setNearbyComparables(comps); try { onNearbyComparables?.(comps); } catch (_) {} }}
            nearbyPreFetchPromise={nearbyPreFetchPromise}
          />
          {gated && (
            <div className="py-4 text-left">
              <button
                type="button"
                onClick={() => { setUserToggledOpen(true); setShowForm(true); }}
                className="inline-flex items-center gap-1 bg-white text-zinc-800 hover:text-zinc-950 font-semibold text-base md:text-sm focus:outline-none border border-zinc-800 px-3 py-1 rounded-md hover:bg-zinc-50 transition-colors"
              >
                {dictionary?.sellingForm?.continueButton || 'Continue'}
                <ChevronDownIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="w-full">
        {gated ? null : (
          <div className={`${isLoadingStats ? 'h-[8rem] md:min-h-0 flex items-center justify-center' : ''}`}>
            {isLoadingStats ? (
              <div className="flex items-center gap-2 text-black p-4 rounded-xl">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                <span className="text-base md:text-sm">{dictionary?.sellingForm?.loadingStats || 'Loading market statistics...'}</span>
              </div>
            ) : stats && (
              <div className="space-y-4 mt-6">
                <div className="space-y-2">
                  {(() => {
                    const template = dictionary?.sellingForm?.statsSummarySentence;
                    // Use PLURAL keys now
                    const translatedPropertyType = (propertyType === 'house' 
                      ? (dictionary?.sellingForm?.propertyTypeHouses || 'houses') 
                      : (dictionary?.sellingForm?.propertyTypeApartments || 'apartments')
                    ).toLowerCase(); // Keep lowercase for sentence flow
                    // Format price with dots as thousands separators, ignoring locale
                    const formattedMedianPrice = stats.medianPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
                    // Format percentage with comma as decimal separator, ignoring locale, and round to one decimal place
                    const formattedGrowthRate = (stats.growthRate * 100).toFixed(1).replace('.', ',');

                    let sentenceHtml = '';
                    if (template) {
                      sentenceHtml = template
                        .replace('[PROPERTY_TYPE]', translatedPropertyType)
                        .replace('[MUNICIPALITY]', cityName)
                        .replace('[GROWTH_RATE]', formattedGrowthRate)
                        .replace('[MEDIAN_PRICE]', formattedMedianPrice);
                    } else {
                    // Fallback sentence if dictionary key is missing
                      sentenceHtml = `Median price for <strong>${translatedPropertyType}</strong> in <strong>${cityName}</strong>: <strong>€${formattedMedianPrice}</strong>, 5-year growth: <strong>${formattedGrowthRate}%</strong>.`;
                    }

                    return (
                      <>
                        <p className="text-base md:text-sm text-zinc-800" dangerouslySetInnerHTML={{ __html: sentenceHtml }} />
                        {/* Source Tag */}
                        <span className="inline-block mt-2 bg-zinc-100 text-zinc-600 text-xs font-medium px-2.5 py-1 rounded-full">
                          {dictionary?.sellingForm?.statsSourceLabel || 'Source'}: Statbel
                        </span>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}
        {!isLoadingStats && !gated && (
          <div>
            <p className="text-zinc-800 mt-4">
              {dictionary?.sellingForm?.statsMessage}
            </p>

            {/* --- Living Area & Plot Area Row (Stage 0+) --- */}
            <div className="grid grid-cols-2 gap-2 mt-2">
              {/* Living Area */}
              <div>
                <div className="relative"> {/* Input container */}
                  <input
                    type="number"
                    name="livingArea"
                    inputMode="numeric"
                    step="1"
                    placeholder={dictionary?.sellingForm?.livingArea || 'Living Area'}
                    // Apply input classes, add padding for indicator
                    className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-xl focus:border-black focus:border-2 outline-none transition-shadow pr-10"
                    value={livingArea}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '' : Number(e.target.value);
                      setLivingArea(value);
                      if (value !== '' && !isNaN(Number(value)) && Number(value) > 0 && visibleStage === 0) {
                        if (propertyType === 'apartment' || (propertyType === 'house' && plotArea !== '' && !isNaN(Number(plotArea)) && Number(plotArea) > 0)) {
                          setVisibleStage(1);
                        }
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                    onKeyDown={(e) => { 
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }
                      if (e.key === '.' || e.key === 'e' || e.key === 'E') { e.preventDefault(); }
                    }} // Prevent arrow key changes and decimal/scientific notation
                  />
                  {/* Static Unit Indicator */}
                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                    m²
                  </span>
                </div>
                {errors.livingArea && <p className="text-red-600 text-sm mt-1">{errors.livingArea}</p>}
              </div>

              {/* Plot Area (House Only) */} 
              {propertyType === 'house' && (
                <div>
                  <div className="relative"> {/* Input container */}
                    <input
                      type="number"
                      name="plotArea"
                      inputMode="numeric"
                      step="1"
                      placeholder={dictionary?.sellingForm?.plotArea || 'Plot Area'}
                      // Apply input classes, add padding for indicator
                      className="w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-xl focus:border-black focus:border-2 outline-none transition-shadow pr-10"
                      value={plotArea}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : Number(e.target.value);
                        setPlotArea(value);
                        if (propertyType === 'house' && value !== '' && !isNaN(Number(value)) && Number(value) > 0 && visibleStage === 0) {
                          if (livingArea !== '' && !isNaN(Number(livingArea)) && Number(livingArea) > 0) {
                            setVisibleStage(1);
                          }
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                      onKeyDown={(e) => { 
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }
                        if (e.key === '.' || e.key === 'e' || e.key === 'E') { e.preventDefault(); }
                      }} // Prevent arrow key changes and decimal/scientific notation
                    />
                    {/* Static Unit Indicator */}
                    <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                      m²
                    </span>
                  </div>
                  {errors.plotArea && <p className="text-red-600 text-sm mt-1">{errors.plotArea}</p>}
                </div>
              )}
            </div>
            {/* --- End Living Area & Plot Area Row --- */}

            {/* --- Bedrooms & Bathrooms Row (Stage 1+) --- */}
            {visibleStage >= 1 && (
              <div className="grid grid-cols-2 gap-2 mt-2">
                {/* Bedrooms (Always shown if row is visible) */} 
                {/* Wrap input and error */}
                <div>
                  <div className="relative"> {/* Input container */}
                    <input
                      type="number"
                      name="bedrooms"
                      inputMode="numeric"
                      step="1"
                      placeholder={dictionary?.sellingForm?.bedrooms || 'Number of Bedrooms'}
                      // Apply input classes, add padding for indicator
                      className={`w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-xl focus:border-black focus:border-2 outline-none transition-shadow ${bedrooms !== '' ? 'pr-24' : ''}`}
                      value={bedrooms}
                      onChange={(e) => {
                        const value = e.target.value === '' ? '' : Number(e.target.value);
                        setBedrooms(value);
                        if (value !== '' && !isNaN(Number(value)) && Number(value) >= 0 && visibleStage === 1) { 
                          setVisibleStage(2);
                        }
                      }}
                      onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                      onKeyDown={(e) => { 
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }
                        if (e.key === '.' || e.key === 'e' || e.key === 'E') { e.preventDefault(); }
                      }} // Prevent arrow key changes and decimal/scientific notation
                    />
                    {/* Dynamic Pluralization Indicator */}
                    {bedrooms !== '' && (
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                        {(typeof bedrooms === 'number' ? bedrooms : Number(bedrooms)) === 1 // Explicit check
                          ? (dictionary?.sellingForm?.bedroomSingular || 'bedroom') 
                          : (dictionary?.sellingForm?.bedroomPlural || 'bedrooms')}
                      </span>
                    )}
                  </div>
                  {errors.bedrooms && <p className="text-red-600 text-sm mt-1">{errors.bedrooms}</p>}
                </div>

                {/* Bathrooms (Stage 2+) */} 
                {visibleStage >= 2 && (
                  <div>
                    <div className="relative"> {/* Input container */}
                      <input
                        type="number"
                        name="bathrooms"
                        inputMode="numeric"
                        step="1"
                        placeholder={dictionary?.sellingForm?.bathrooms || 'Number of Bathrooms'}
                        // Apply input classes, add padding for indicator
                        className={`w-full px-3 py-2 text-base md:text-sm bg-white border border-zinc-300 rounded-xl focus:border-black focus:border-2 outline-none transition-shadow ${bathrooms !== '' ? 'pr-24' : ''}`}
                        value={bathrooms}
                        onChange={(e) => {
                          const value = e.target.value === '' ? '' : Number(e.target.value);
                          setBathrooms(value);
                          if (value !== '' && !isNaN(Number(value)) && Number(value) >= 0 && visibleStage === 2) {
                            setVisibleStage(3);
                          }
                        }}
                        onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                        onKeyDown={(e) => { 
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); }
                          if (e.key === '.' || e.key === 'e' || e.key === 'E') { e.preventDefault(); }
                        }} // Prevent arrow key changes and decimal/scientific notation
                      />
                      {/* Dynamic Pluralization Indicator */}
                      {bathrooms !== '' && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-sm text-zinc-500 pointer-events-none">
                          {(typeof bathrooms === 'number' ? bathrooms : Number(bathrooms)) === 1 // Explicit check
                            ? (dictionary?.sellingForm?.bathroomSingular || 'bathroom') 
                            : (dictionary?.sellingForm?.bathroomPlural || 'bathrooms')}
                        </span>
                      )}
                    </div>
                    {errors.bathrooms && <p className="text-red-600 text-sm mt-1">{errors.bathrooms}</p>}
                  </div>
                )}
              </div>
            )}
            {/* --- End Bedrooms & Bathrooms Row --- */}

            {propertyType === 'house' && (
              <> { /* Plot/Facade only shown for houses from stage 3 */}
                {/* --- Facade Radios (Stage 3+ House only) --- */} 
                {visibleStage >= 3 && (
                  <div className="mt-2">
                    <div className="grid grid-cols-3 items-center gap-1">
                      {/* Facade Option 2 - Apply AddressForm styling */}
                      <label htmlFor="facadeOption2" className="relative flex items-center justify-center cursor-pointer px-1 py-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                        <input
                          type="radio"
                          id="facadeOption2"
                          name="facadeValue"
                          value={2}
                          checked={facadeValue === 2}
                          onChange={() => {
                            setFacadeValue(2);
                            if (visibleStage === 3) setVisibleStage(4);
                          }}
                          // Make input cover label area to avoid scroll-into-view jump
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        {/* Add ml-2 and text-zinc-800 */}
                        <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-1">
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {dictionary?.sellingForm?.facadeOption2 || '2'}
                          </span>
                          {facadeValue === 2 && (
                            <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </label>
                      {/* Facade Option 3 - Apply AddressForm styling */}
                      <label htmlFor="facadeOption3" className="relative flex items-center justify-center cursor-pointer px-3 py-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                        <input
                          type="radio"
                          id="facadeOption3"
                          name="facadeValue"
                          value={3}
                          checked={facadeValue === 3}
                          onChange={() => {
                            setFacadeValue(3);
                            if (visibleStage === 3) setVisibleStage(4);
                          }}
                          // Make input cover label area to avoid scroll-into-view jump
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        {/* Add ml-2 and text-zinc-800 */}
                        <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-1">
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {dictionary?.sellingForm?.facadeOption3 || '3'}
                          </span>
                          {facadeValue === 3 && (
                            <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </label>
                      {/* Facade Option 4 - Apply AddressForm styling */}
                      <label htmlFor="facadeOption4" className="relative flex items-center justify-center cursor-pointer px-1 py-3 border bg-white rounded-xl hover:bg-zinc-50 transition-colors border-zinc-300 has-[:checked]:border-black has-[:checked]:border-2">
                        <input
                          type="radio"
                          id="facadeOption4"
                          name="facadeValue"
                          value={4}
                          checked={facadeValue === 4}
                          onChange={() => {
                            setFacadeValue(4);
                            if (visibleStage === 3) setVisibleStage(4);
                          }}
                          // Make input cover label area to avoid scroll-into-view jump
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        {/* Add ml-2 and text-zinc-800 */}
                        <span className="text-base md:text-sm text-zinc-800 font-medium flex items-center gap-1">
                          <span className="whitespace-nowrap overflow-hidden text-ellipsis">
                            {dictionary?.sellingForm?.facadeOption4 || '4+'}
                          </span>
                          {facadeValue === 4 && (
                            <svg className="w-4 h-4 text-black flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Submit Button (Stage 3+ for Apt and House) */}
            {((propertyType === 'apartment' && visibleStage >= 3) || (propertyType === 'house' && visibleStage >= 4)) && (
              <div className="mt-4">
                <button
                  type="submit"
                  disabled={disableSubmit}
                  // Apply button classes using constants
                  className="px-4 py-2.5 bg-zinc-800 text-white rounded-lg text-sm font-medium transition-colors hover:bg-zinc-700 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {dictionary?.sellingForm?.continueButton || 'Continue'}
                </button>
              </div>
            )}
          </div>
        )}
      </form>

      <style jsx>{`
        .input-container { 
          overflow: hidden;
        };
      `}</style>
    </>
  );
} 